import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface ForecastCardProps {
  type: 'sprint' | 'project' | 'effort';
  id: string;
  storyPoints?: number;
  teamId?: string;
  projectId?: string;
  onRefresh?: () => void;
}

const ForecastCard: React.FC<ForecastCardProps> = ({
  type,
  id,
  storyPoints,
  teamId,
  projectId,
  onRefresh
}) => {
  const [forecast, setForecast] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if we have valid required data
    if (type === 'effort' && (!storyPoints || storyPoints <= 0)) {
      return; // Don't fetch if storyPoints is invalid
    }
    fetchForecast();
  }, [type, id, storyPoints]);

  const fetchForecast = async () => {
    try {
      setLoading(true);
      setError(null);
      let result;

      if (type === 'sprint') {
        result = await apiService.forecastSprintCompletion(id);
      } else if (type === 'project') {
        result = await apiService.forecastProjectTimeline(id);
      } else if (type === 'effort' && storyPoints) {
        // Validate storyPoints before making API call
        const points = Number(storyPoints);
        if (isNaN(points) || points <= 0 || points > 1000) {
          setError('Invalid story points. Must be between 1 and 1000.');
          return;
        }
        result = await apiService.calculateEffortConfidence({
          storyPoints: points,
          teamId,
          projectId
        });
      }

      setForecast(result);
    } catch (err: any) {
      setError(err.message || 'Failed to get forecast');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 0.7) return 'success';
    if (probability >= 0.5) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Generating forecast...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!forecast) return null;

  // Sprint Forecast
  if (type === 'sprint' && forecast.sprintId) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">Sprint Completion Forecast</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Probability of On-Time Completion
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <LinearProgress
                    variant="determinate"
                    value={forecast.probabilityOnTime * 100}
                    sx={{ flex: 1 }}
                    color={getProbabilityColor(forecast.probabilityOnTime)}
                  />
                  <Typography variant="h6">
                    {(forecast.probabilityOnTime * 100).toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Predicted Completion
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {formatDate(forecast.predictedCompletionDate)}
                </Typography>
              </Box>
            </Grid>

            {forecast.scenarios && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Scenarios
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={`Optimistic: ${formatDate(forecast.scenarios.optimistic.completionDate)}`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`Realistic: ${formatDate(forecast.scenarios.realistic.completionDate)}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`Pessimistic: ${formatDate(forecast.scenarios.pessimistic.completionDate)}`}
                    color="warning"
                    size="small"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  // Project Forecast
  if (type === 'project' && forecast.projectId) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">Project Timeline Forecast</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Predicted Completion
              </Typography>
              <Typography variant="h5" sx={{ mt: 1 }}>
                {formatDate(forecast.predictedCompletionDate)}
              </Typography>
              <Chip
                label={`${(forecast.confidence * 100).toFixed(0)}% Confidence`}
                color={getProbabilityColor(forecast.confidence)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Grid>

            {forecast.completionProbability && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Completion Probabilities
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={`On Time: ${(forecast.completionProbability.onTime * 100).toFixed(0)}%`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`Delayed: ${(forecast.completionProbability.delayed * 100).toFixed(0)}%`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    label={`Early: ${(forecast.completionProbability.early * 100).toFixed(0)}%`}
                    color="info"
                    size="small"
                  />
                </Box>
              </Grid>
            )}

            {forecast.riskFactors && forecast.riskFactors.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WarningIcon color="warning" />
                  <Typography variant="subtitle2">Risk Factors</Typography>
                </Box>
                {forecast.riskFactors.map((risk: any, index: number) => (
                  <Alert key={index} severity={risk.severity} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>{risk.factor}:</strong> {risk.impact}
                    </Typography>
                  </Alert>
                ))}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  // Effort Confidence
  if (type === 'effort' && forecast.confidenceIntervals) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Effort Confidence Intervals</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Estimated Effort
                </Typography>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {forecast.estimatedEffort.toFixed(1)} Story Points
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Confidence Intervals
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip
                  label={`Optimistic: ${forecast.confidenceIntervals.optimistic.toFixed(1)}`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`Realistic: ${forecast.confidenceIntervals.realistic.toFixed(1)}`}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`Pessimistic: ${forecast.confidenceIntervals.pessimistic.toFixed(1)}`}
                  color="warning"
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Chip
                label={`${forecast.riskLevel.toUpperCase()} Risk`}
                color={forecast.riskLevel === 'low' ? 'success' : forecast.riskLevel === 'medium' ? 'warning' : 'error'}
                size="small"
              />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {forecast.reasoning}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default ForecastCard;

